/**
 * One consent state for itsbaba.com, app.itsbaba.com and news.itsbaba.com
 * (ad-measurement contract 2026-10-20, section 4). The browser keeps it in a
 * first-party cookie on `.itsbaba.com`, so a choice made on one host holds on
 * the others, and the API reads the same shape from checkout signals.
 *
 * The runtime lives in consent-boot.ts (a string that runs before any tag);
 * this file is the typed reading side and the cookie codec the tests hold the
 * runtime to.
 */
export const CONSENT_COOKIE = 'baba_consent'
export const CONSENT_VERSION = 1
export const CONSENT_OPEN_EVENT = 'baba:consent-open'
export const CONSENT_REGION_URL = 'https://e.itsbaba.com/consent/region'

export type ConsentValue = 'granted' | 'denied'
export const CONSENT_KEYS = ['ad_storage', 'ad_user_data', 'ad_personalization', 'analytics_storage'] as const
export type ConsentKey = (typeof CONSENT_KEYS)[number]

export interface ConsentState extends Record<ConsentKey, ConsentValue> {
  /** ISO country code from the region endpoint, or '' when it could not be asked. */
  region: string
  version: number
  /** When the visitor chose, or null while the state is only a default. */
  decided_at: string | null
  /** US "Do not sell or share": Meta LDU and TikTok limited data use. Absent means off. */
  do_not_sell?: boolean
}

/** What the page can ask the gate; `window.babaConsent` implements it. */
export interface ConsentGate {
  /** True once the cookie, the region or a failure has settled the state. */
  ready(): boolean
  get(): ConsentState | null
  /** The visitor's region requires an explicit choice (EEA, UK, CH, or the question failed). */
  requiresPrompt(): boolean
  /** A choice is owed: required here and not yet made. */
  needsChoice(): boolean
  allowed(key: ConsentKey): boolean
  /** Runs `fn` once, as soon as `key` is granted (immediately if it already is). Never runs if it is not. */
  when(key: ConsentKey, fn: () => void): void
  subscribe(fn: (state: ConsentState) => void): () => void
  accept(): void
  reject(): void
  save(choice: Partial<Record<ConsentKey, ConsentValue>>): void
  /** US opt out of sale or sharing, on or off. */
  optOutOfSale(on: boolean): void
  open(): void
  gpc: boolean
}

export function allConsent(value: ConsentValue, region = ''): ConsentState {
  return { ad_storage: value, ad_user_data: value, ad_personalization: value, analytics_storage: value, region, version: CONSENT_VERSION, decided_at: null }
}

/** Validate a cookie value (still URI-encoded or already decoded). Anything off-shape is no state. */
export function parseConsentCookie(raw: string | null | undefined): ConsentState | null {
  if (!raw) return null
  try {
    let text = raw
    if (!text.startsWith('{')) text = decodeURIComponent(text)
    const value = JSON.parse(text) as Record<string, unknown>
    if (!value || typeof value !== 'object' || value.version !== CONSENT_VERSION) return null
    for (const key of CONSENT_KEYS) if (value[key] !== 'granted' && value[key] !== 'denied') return null
    if (typeof value.region !== 'string' || value.region.length > 8) return null
    if (value.decided_at !== null && typeof value.decided_at !== 'string') return null
    return {
      ad_storage: value.ad_storage as ConsentValue,
      ad_user_data: value.ad_user_data as ConsentValue,
      ad_personalization: value.ad_personalization as ConsentValue,
      analytics_storage: value.analytics_storage as ConsentValue,
      region: value.region,
      version: CONSENT_VERSION,
      decided_at: value.decided_at as string | null,
      ...(value.do_not_sell === true ? { do_not_sell: true } : {}),
    }
  } catch { return null }
}

export function readConsentFromCookies(cookies: string): ConsentState | null {
  const row = cookies.split(';').map(part => part.trim()).find(part => part.startsWith(`${CONSENT_COOKIE}=`))
  return row ? parseConsentCookie(row.slice(CONSENT_COOKIE.length + 1)) : null
}

/** The state as the API receives it (apps/api lib/ad-consent.ts normalizeConsent). */
export function consentForApi(state: ConsentState | null): Record<string, unknown> | undefined {
  if (!state) return undefined
  const { ad_storage, ad_user_data, ad_personalization, analytics_storage, region, version, decided_at, do_not_sell } = state
  // apps/api reads version as a string and an undecided default as no decided_at.
  return {
    ad_storage, ad_user_data, ad_personalization, analytics_storage, region, version: String(version),
    ...(decided_at ? { decided_at } : {}), ...(do_not_sell ? { do_not_sell: true } : {}),
  }
}
